/**
 * Preparation Steps API - Doctor Recipe Steps CRUD Operations
 * Handles getting, creating, updating, and deleting recipe preparation steps
 */

import http from '../../../client';
import { errorHandler } from '../../../errorHandler';
import type { ApiResponse } from '../../../types';
import type { PreparationStep, CreatePreparationStepData } from './types';
import { extractResponseData, buildStepFormData } from './helpers';
import { transformStepFromBackend } from './transformers';

/**
 * Gets all preparation steps for a remedy
 * Endpoint: GET /recipes/{recipeId}/preparation-steps
 * @param recipeId - ID of the remedy
 * @returns Array of preparation steps
 */
export const getRecipePreparationSteps = async (
  recipeId: string | number,
): Promise<ApiResponse<PreparationStep[]>> => {
  try {
    const recipeIdStr = String(recipeId);
    const response = await http.get(
      `/recipes/${recipeIdStr}/preparation-steps`,
    );

    // Extract data from response (handles nested { success, data: [...] } structure)
    const extractedData = extractResponseData(response);

    // Map backend response to frontend format
    const steps = Array.isArray(extractedData)
      ? extractedData.map((step) => transformStepFromBackend(step))
      : [];

    return {
      success: true,
      data: steps,
    };
  } catch (error) {
    return {
      success: false,
      error: errorHandler(error),
    };
  }
};

/**
 * Adds a new preparation step to a remedy
 * Endpoint: POST /recipes/{recipeId}/preparation-steps
 * @param recipeId - ID of the remedy
 * @param step - Step data to add (includes image/video files)
 * @returns Created step object
 */
export const saveRecipePreparationStep = async (
  recipeId: string | number,
  step: CreatePreparationStepData,
): Promise<ApiResponse<PreparationStep>> => {
  try {
    const recipeIdStr = String(recipeId);
    const formData = buildStepFormData(step);

    const response = await http.post(
      `/recipes/${recipeIdStr}/preparation-steps`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    );

    // Extract and transform response
    const stepData = extractResponseData(response);
    const mappedStep = transformStepFromBackend(stepData);

    return {
      success: true,
      data: mappedStep,
    };
  } catch (error) {
    return {
      success: false,
      error: errorHandler(error),
    };
  }
};

/**
 * Updates an existing preparation step in a remedy
 * Endpoint: POST /recipes/{recipeId}/preparation-steps
 * @param recipeId - ID of the remedy
 * @param stepId - ID of the step to update
 * @param step - Updated step data (includes image/video files)
 * @returns Updated step object
 */
export const updateRecipePreparationStep = async (
  recipeId: string | number,
  stepId: string | number,
  step: CreatePreparationStepData,
): Promise<ApiResponse<PreparationStep>> => {
  try {
    const recipeIdStr = String(recipeId);
    const formData = buildStepFormData(step, stepId);

    const response = await http.post(
      `/recipes/${recipeIdStr}/preparation-steps`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    );

    // Extract and transform response
    const stepData = extractResponseData(response);
    const mappedStep = transformStepFromBackend(stepData);

    return {
      success: true,
      data: mappedStep,
    };
  } catch (error) {
    return {
      success: false,
      error: errorHandler(error),
    };
  }
};

/**
 * Deletes a preparation step from a remedy
 * Endpoint: DELETE /recipes/{recipeId}/preparation-steps/{stepId}
 * @param recipeId - ID of the remedy
 * @param stepId - ID of the step to delete
 */
export const deleteRecipePreparationStep = async (
  recipeId: string | number,
  stepId: string | number,
): Promise<ApiResponse<void>> => {
  try {
    const recipeIdStr = String(recipeId);
    const stepIdStr = String(stepId);

    await http.delete(`/recipes/${recipeIdStr}/preparation-steps/${stepIdStr}`);

    return {
      success: true,
      data: undefined,
    };
  } catch (error) {
    return {
      success: false,
      error: errorHandler(error),
    };
  }
};

/**
 * Reorders preparation steps by step IDs
 * Endpoint: PATCH /recipes/{recipeId}/preparation-steps/reorder
 * @param recipeId - ID of the remedy
 * @param stepIds - Array of step IDs in desired display order
 */
export const reorderRecipePreparationSteps = async (
  recipeId: string | number,
  stepIds: (string | number)[],
): Promise<ApiResponse<void>> => {
  try {
    const recipeIdStr = String(recipeId);
    await http.patch(`/recipes/${recipeIdStr}/preparation-steps/reorder`, {
      stepIds: stepIds.map((id) =>
        typeof id === 'string' ? parseInt(id, 10) : id,
      ),
    });

    return {
      success: true,
      data: undefined,
    };
  } catch (error) {
    return {
      success: false,
      error: errorHandler(error),
    };
  }
};

/**
 * Deletes media (image/video) from a preparation step
 * Endpoint: DELETE /recipes/{recipeId}/preparation-steps/{stepId}/media/{type}
 * @param recipeId - ID of the remedy
 * @param stepId - ID of the step
 * @param type - 'image' | 'video'
 */
export const deleteRecipePreparationStepMedia = async (
  recipeId: string | number,
  stepId: string | number,
  type: 'image' | 'video',
): Promise<ApiResponse<PreparationStep>> => {
  try {
    const recipeIdStr = String(recipeId);
    const stepIdStr = String(stepId);

    const response = await http.delete(
      `/recipes/${recipeIdStr}/preparation-steps/${stepIdStr}/media/${type}`,
    );

    // Extract and transform response
    const stepData = extractResponseData(response);
    const mappedStep = transformStepFromBackend(stepData);

    return {
      success: true,
      data: mappedStep,
    };
  } catch (error) {
    return {
      success: false,
      error: errorHandler(error),
    };
  }
};
